import React from 'react';

import {CODON_LENGTH} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {DisplayAminoAcid, getTranslationAADisplayProperties} from 'molbio/maps/linear/TranslationDisplayUtil';
import Type from 'molbio/maps/Type';
import {AminoAcid} from 'molbio/MolBioConstants';
import * as sequtil from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import getTestProps from 'test/lib/cypress/getTestProps';

function shouldRenderAminoAcidIndex(
  aa: AminoAcid,
  aminoAcidIndicesVisible: boolean,
  basesCovered: number,
  sequence: Sequence,
  translation: Translation
): boolean {
  return (
    aminoAcidIndicesVisible &&
    // If the amino acid is cut off or split across multiple slices, then only render
    // the index when this rendering covers at least two bases.
    basesCovered >= 2 &&
    // If the entire translation group covers more than 100 residues (i.e. 300 bases),
    // then only render every other index since it takes more horizontal space to render
    // 3+ digits.
    // MINOR BUG: this calculation does not necessarily hold true for alignments; since a residue
    // could cover one or more gaps, calculating number of translation residues as length / 3 could
    // be an overestimate.
    (translation.totalGroupLength(sequence) < CODON_LENGTH * 100 || aa.aaIndex % 2 === 0)
  );
}

interface AminoAcidIndexProps {
  aa: AminoAcid;
  aminoAcidIndicesVisible: boolean;
  basesCovered: number;
  indexType: Type;
  getBaseWidth: (bases: number) => number;
  sequence: Sequence;
  translation: Translation;
}

function AminoAcidIndex({
  aa,
  aminoAcidIndicesVisible,
  basesCovered,
  indexType,
  getBaseWidth,
  sequence,
  translation,
}: AminoAcidIndexProps): JSX.Element {
  // Calculate how many characters are needed to render the AA index.
  const indexCharCount = String(aa.aaIndex).length;
  // Once the index surpasses 3 digits, switch to a smaller font type.
  const type = indexCharCount <= 3 ? indexType : new Type(indexType.fontSize - 2);
  let x: number;
  if (basesCovered >= 2) {
    // Calculate total width of amino acid rendering and subtract width needed to render the index to
    // arrive at the padding available. Distribute evenly to each side by dividing by 2.
    x = (getBaseWidth(basesCovered) - type._width * indexCharCount) / 2;
  } else {
    x = 0;
  }

  return (
    <text
      className="amino-acid-index"
      fontSize={type.fontSize}
      x={x}
      y={type.height() - 3}
      pointerEvents="none"
      {...getTestProps({component: 'TranslationAminoAcid', element: 'index'})}
    >
      {shouldRenderAminoAcidIndex(aa, aminoAcidIndicesVisible, basesCovered, sequence, translation) &&
        aa.aaIndex}
    </text>
  );
}

interface AminoAcidShapeProps {
  aa: DisplayAminoAcid;
  basesCovered: number;
  getBaseWidth: (bases: number) => number;
  strokeColor: string | null;
  translation: Translation;
  fontType: Type;
  indexType: Type;
  aminoAcidIndicesVisible: boolean;
  fontOpacityFactor: number;
  handleClick: (e: React.MouseEvent, start: number, end: number) => void;
  handleDoubleClick: (e: React.MouseEvent) => void;
  handleContextMenu: (e: React.MouseEvent) => void;
}
/**
 * Renders the arrow shape and residue for one amino acid.
 */
function AminoAcidShape({
  aa,
  basesCovered,
  getBaseWidth,
  strokeColor,
  translation,
  fontType,
  indexType,
  aminoAcidIndicesVisible,
  fontOpacityFactor,
  handleClick,
  handleDoubleClick,
  handleContextMenu,
}: AminoAcidShapeProps): JSX.Element {
  const fontHeight = fontType.height();
  const baseWidth = getBaseWidth(aa.bp);
  const arrowWidth = (fontType._width + fontType.letterSpacing) / 2;
  const width = baseWidth + arrowWidth;
  const pathD = `M 0 0 L ${baseWidth} 0 L ${width} ${fontHeight / 2} L ${baseWidth} ${fontHeight} \
  L 0 ${fontHeight} L ${width - baseWidth} ${fontHeight / 2} L 0 0`;

  // Shift AA arrows downwards to accommodate AA indices when shown
  const translate = `translate(0, ${aminoAcidIndicesVisible ? indexType.height() : 0})`;
  let transform = translate;
  if (translation.strand() === -1) {
    transform = `${translate} rotate(180 ${getBaseWidth(basesCovered) / 2} ${fontHeight / 2})`;
  }
  const displayProperties = getTranslationAADisplayProperties(aa, translation.selected(), 1);

  let residueX: number;
  if (basesCovered >= 3) {
    // Calculate total width of amino acid rendering and subtract width needed to render the residue (always
    // a single character so just fontType._width) to arrive at the padding available. Distribute evenly to
    // each side by dividing by 2.
    residueX = (getBaseWidth(basesCovered) - fontType._width) / 2;
  } else if (basesCovered === 2) {
    residueX = getBaseWidth(1) + fontType.letterSpacing;
  } else {
    residueX = 0;
  }
  const residueY = aminoAcidIndicesVisible
    ? indexType.height() + fontType.fontSize / 2
    : fontType.fontSize / 2;

  function handleMouseDown(e: React.MouseEvent): void {
    handleClick(e, aa.indexStart, aa.indexEnd);
  }

  function onContextMenu(e: React.MouseEvent): void {
    handleMouseDown(e);
    handleContextMenu(e);
  }

  return (
    <g
      pointerEvents="all"
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onContextMenu={onContextMenu}
    >
      <path
        d={pathD}
        shapeRendering="geometricPrecision"
        transform={transform}
        stroke={strokeColor ?? undefined}
        fill={displayProperties.aaFillColor}
        fillOpacity={displayProperties.fillOpacity}
        className={`translation-${translation.uid()}`}
        {...getTestProps({component: 'TranslationAminoAcid', element: 'shape'})}
      />
      <text
        className="amino-acid"
        fontSize={fontType.fontSize}
        fill={displayProperties.aaGlyphColor}
        fontWeight={displayProperties.fontWeight}
        fillOpacity={fontOpacityFactor}
        x={residueX}
        y={residueY}
        pointerEvents="none"
        dominantBaseline="central"
        {...getTestProps({component: 'translationLayeredView', element: 'aminoAcidResidue'})}
      >
        {basesCovered > 1 && aa.b}
      </text>
    </g>
  );
}

export interface TranslationAminoAcidProps {
  aa: DisplayAminoAcid;
  aminoAcidIndicesVisible: boolean;
  fontType: Type;
  indexType: Type;
  translation: Translation;
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  sequence: Sequence;
  fontOpacityFactor: number;
  handleClick: (e: React.MouseEvent, start: number, end: number) => void;
  handleDoubleClick: (e: React.MouseEvent) => void;
  openContextMenu: (e: React.MouseEvent) => void;
}

/**
 * Renders the visual for amino acid residues of a translation on the sequence map.
 * Equivalent to the elements rendered by TranslationLayeredView Backbone view.
 */
export default function TranslationAminoAcid({
  aa,
  aminoAcidIndicesVisible,
  fontType,
  indexType,
  translation,
  sequence,
  sliceStart,
  sliceEnd,
  xFn,
  fontOpacityFactor,
  handleClick,
  handleDoubleClick,
  openContextMenu,
}: TranslationAminoAcidProps): JSX.Element {
  // Each whole amino acid residue covers 3 indices, so need to clamp in case of partial residues or circular wraps.
  const startPos = aa.indexStart > sliceEnd ? sliceStart : Math.max(aa.indexStart, sliceStart);
  const endPos = aa.indexEnd < sliceStart ? sliceEnd : Math.min(sliceEnd, aa.indexEnd);
  const basesCovered = sequtil.length(startPos, endPos, sequence.length()) % sequence.length();
  const getBaseWidth = (bases: number): number => bases * (fontType._width + fontType.letterSpacing);

  return (
    <g
      className="amino-acid-group"
      transform={`translate(${xFn(startPos)},0)`}
      {...getTestProps({component: 'TranslationAminoAcid', element: 'root'})}
    >
      <AminoAcidShape
        aa={aa}
        getBaseWidth={getBaseWidth}
        strokeColor={null}
        translation={translation}
        basesCovered={basesCovered}
        fontType={fontType}
        indexType={indexType}
        aminoAcidIndicesVisible={aminoAcidIndicesVisible}
        fontOpacityFactor={fontOpacityFactor}
        handleClick={handleClick}
        handleDoubleClick={handleDoubleClick}
        handleContextMenu={openContextMenu}
      />
      <AminoAcidIndex
        aa={aa}
        aminoAcidIndicesVisible={aminoAcidIndicesVisible}
        basesCovered={basesCovered}
        getBaseWidth={getBaseWidth}
        indexType={indexType}
        sequence={sequence}
        translation={translation}
      />
    </g>
  );
}
